import { WaitlistBand } from "../components/WaitlistBand";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 pb-24">
        {/* Header — kept on the left, starting the page */}
        <div className="max-w-2xl mb-16 lg:mb-20">
          <div className="overline text-[var(--gu-muted)] mb-4">Our story</div>
          <h1 className="font-display text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">
            A studio<br />under an umbrella.
          </h1>
        </div>

        {/* Story body — two-column flow with a soft photo placeholder sitting BEHIND the text */}
        <div className="relative">
          {/* Soft visible placeholder (photo pending) — full-bleed behind the columns */}
          <div
            aria-hidden="true"
            data-testid="about-photo-placeholder"
            className="absolute -inset-x-4 -inset-y-6 lg:-inset-x-10 lg:-inset-y-10 pointer-events-none z-0"
          >
            <div className="relative w-full h-full border border-dashed border-[var(--gu-border)] bg-[var(--gu-surface-2)]/45">
              <div className="absolute top-4 left-4 overline text-[var(--gu-muted)]/70">
                A photo, held for later
              </div>
              <div className="absolute bottom-4 right-4 overline text-[var(--gu-muted)]/50">
                placeholder
              </div>
            </div>
          </div>

          {/* Story text in two balanced columns, above the placeholder */}
          <div
            className="relative z-10 columns-1 lg:columns-2 gap-x-12 text-[var(--gu-muted)] leading-relaxed text-lg [&>p]:mb-6 [&>p]:break-inside-avoid"
            data-testid="about-story-columns"
          >
            <p>
              I grew up in a small town in Minnesota — public school, cold winters, and
              every summer and holiday I could manage, my brother and I stayed on my
              grandparents’, aunts’, and uncles’ farms in South Dakota. When we weren’t
              at the farm, you could find me camping with my dad on Middle Lake in Otter
              Tail County or in the Boundary Waters, under a canoe and portaging deep
              across the territory for weeks. My mother is one of fourteen. The farm
              gave me a thumb to grow things with, an understanding of economy, and a
              lot of cousins. My father’s quiet wonderings and the water gave me the
              rest.
            </p>
            <p>
              My father is a pacifist and a naturalist who enlisted and served in
              Vietnam anyway. I believe he’s an angel who has never told me so. From
              him, I learned unconditional love, nonjudgment, mindfulness, and how to
              move through the woods without disturbing anything. No loud engines. No
              guns. No fishing unless we planned to eat. He taught me a slow reverence
              for small, quiet, budding creatures. From my mother, I learned a kind of
              dedication that doesn’t know where its own edges are.
            </p>
            <p>
              I studied biology because I wanted to witness and understand the miracle
              of how God works in every living thing.  But long before I had words for
              that wonder, I had already learned how vulnerable a life can be.
            </p>
            <p>
              I learned about the foster system at far too young an age. My best friend
              was taken from our classroom and placed into foster care in second grade,
              and then again in fourth. I’ll never forget him standing, walking quietly
              to the door, and leaving without a word. He stopped, turned, gave me one
              last look, and smiled with a broken heart. His eyes said everything I
              never wanted to see — and I’ve been looking out for the vulnerable in
              society ever since.
            </p>
            <p>
              I’ve stayed in society’s trenches, and I intend to continue. No one is
              worth less than anyone else — and no one is worth more, either. I keep
              company with those who already know that, and who try to speak only love.
            </p>
            <p>
              Grace Under Umbrella began sideways, without a plan. When I moved to
              Southern California, I wanted to stop hanging pictures and decor on my
              walls and start keeping plants there instead. One became many. Many
              became a greenhouse. Then came a room full of picture windows facing the
              intense California sun and wild ocean, and a shelf — or a dozen — of
              grow-light stations for the shade lovers.
            </p>
            <p>
              Grace is my Doberman. Lady is my Lab. Both are rescues with tough
              beginnings, both are my whole world today, and both, in their own way,
              are the reason for the name.
            </p>
            <p>
              Grace Under Umbrella. A tender shelter.
            </p>
            <p>
              The idea that vulnerable, unfussy, beautiful things deserve a canopy held
              over them until they’re ready for whatever comes next — beautifully.
              <br />
              Twenty percent of profit will go to a foundation, still to be named,
              supporting young people transitioning out of foster care. They need
              homes, protection from the trenches, education, and the opportunity to
              learn a trade.
            </p>
            <p>
              Every plant here is propagated by hand — in the greenhouse under Southern
              California light, or beneath grow lights for the ferns and tricky
              calatheas that prefer their sun with shade.
            </p>
            <p>
              We’ll open the doors to other small growers soon. There are more of us
              than you’d think: people with spare bedrooms full of propagations,
              potters throwing on weekends, and indoor-jungle homes that need a site
              uncomplicated by extra expenses and unclear fees.
            </p>
            <p>
              The big marketplaces weren’t built for us — or for you.
            </p>
            <p>
              So we’re building this one instead.
            </p>
            <p>
              Slowly. Under the umbrella.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[var(--gu-surface-2)] border-y border-[var(--gu-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
          <Value title="Slow" body="Nothing ships before it's ready. Roots checked twice. Soil actually alive." />
          <Value title="Small" body="Small studio, small batches. If it's here, it was tended by hand." />
          <Value title="Sheltering" body="A canopy for other growers, and for kids the world was too fast for." />
        </div>
      </section>

      <WaitlistBand />
    </div>
  );
}

function Value({ title, body }) {
  return (
    <div>
      <div className="font-display text-4xl mb-4">{title}</div>
      <p className="text-[var(--gu-muted)] leading-relaxed">{body}</p>
    </div>
  );
}
